import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ControlPoint } from '../../../shared/models/control-points/control-point.model';
import { ControlPointService } from '../../../shared/services/control-points/control-point.service';

@Component({
  selector: 'app-control-points-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    CardModule,
    TableModule,
    TagModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './control-points-page.component.html',
  styleUrl: './control-points-page.component.scss',
})
export class ControlPointsPageComponent implements OnInit {
  service = inject(ControlPointService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #router = inject(Router);

  ngOnInit(): void {
    this.service.getAll();
  }

  navigateToCreate() {
    this.#router.navigate(['/control-points/control-points/new']);
  }

  navigateToDetail(item: ControlPoint) {
    this.#router.navigate(['/control-points/control-points', item.id]);
  }

  qualificationCount(item: ControlPoint): number {
    return Array.isArray(item.required_qualifications)
      ? item.required_qualifications.length
      : 0;
  }

  qualificationNames(item: ControlPoint): string[] {
    if (!Array.isArray(item.required_qualifications)) return [];
    return item.required_qualifications
      .map((q: { name?: string }) => q?.name)
      .filter((name): name is string => typeof name === 'string');
  }

  onRowDelete(event: Event, item: ControlPoint) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'controlPoints.controlPoints.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'controlPoints.controlPoints.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.service.delete(item.id).subscribe({
          next: () => {
            this.service.getAll();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant(
                'controlPoints.controlPoints.deleted',
              ),
            });
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translate.instant('label.errorHeader'),
              detail: this.#translate.instant(
                'controlPoints.controlPoints.deleteError',
              ),
            });
          },
        });
      },
    });
  }
}
